/* =====================================================================================
   Arena — the go-to-market strategy.

   Named for the gladiatorial sense, and restricted for the obvious one.

   THE AI DRAFT IS A DRAFT AND SAYS SO. Forum holds every input a strategy needs — market size,
   waiting lists, who holds each budget, the incumbent at each centre, the asset portfolio, the
   live pipeline — and can assemble a first pass from them. That pass is a starting position,
   not a conclusion, and the difference is on the page rather than implied. Each move stays
   flagged as machine-drafted until a person edits it, and the flag clears on that edit rather
   than on a separate confirmation nobody would remember to give.

   TWO DOORS INTO A RESTRICTED ROOM. Editing what the company intends to do is deliberately
   narrow. But a restriction with no route through it produces shadow documents and corridor
   conversations, so there are two: ask for access, or raise a suggestion without needing any.
   The second is open to everyone and is the more valuable of the two, because restricting who
   may EDIT a strategy is sensible and restricting who may say it is wrong is not.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import {
  mayReadArena, mayEditArena, fetchStrategies, fetchMoves, saveMove,
  fetchSuggestions, raiseSuggestion, requestArenaAccess,
  type Strategy, type Move, type Suggestion,
} from '../lib/arena';
import { onFail } from '../lib/errors';
import { useDialog } from '../lib/ui';
import { Boundary } from '../components/Boundary';
import './arena.css';

const EFFORT_TONE: Record<string, string> = {
  low: 'ok', medium: 'info', high: 'warn', very_high: 'risk',
};

function ArenaView() {
  const [canRead, setCanRead] = useState<boolean | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [moves, setMoves] = useState<Move[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [editing, setEditing] = useState<Move | null>(null);
  const [suggesting, setSuggesting] = useState<{ move: Move | null } | null>(null);

  useEffect(() => {
    mayReadArena().then(setCanRead).catch(() => setCanRead(false));
    mayEditArena().then(setCanEdit).catch(() => setCanEdit(false));
  }, []);

  const reload = useCallback(() => {
    fetchStrategies().then(async (s) => {
      setStrategies(s);
      if (s[0]) setMoves(await fetchMoves(s[0].id));
    }).catch(onFail('The strategy', () => setStrategies([])));
    fetchSuggestions().then(setSuggestions).catch(onFail('Suggestions', () => setSuggestions([])));
  }, []);

  useEffect(() => { if (canRead) reload(); }, [canRead, reload]);

  // Suggestions are open to everyone, so this is shown even to people who cannot read the
  // strategy. Somebody who knows the market should not need clearance to say something.
  if (canRead === false) return <NoAccess onSuggest={() => setSuggesting({ move: null })}
                                          suggesting={suggesting}
                                          onCloseSuggest={() => setSuggesting(null)} />;
  if (canRead === null) return <p className="quiet-loading">Checking access</p>;

  const strategy = strategies[0];
  const openSuggestions = suggestions.filter((s) => s.status === 'open');

  return (
    <div className="page arena">
      <div className="page-head">
        <h1 className="page-title">Arena</h1>
        <p className="page-sub">
          Where the market model, the assets, the reimbursement routes and the competition
          become a sequence of decisions.
        </p>
      </div>

      {!strategy && (
        <p className="module-empty">
          No strategy drafted. Arena assembles a first pass from what Forum already holds:
          market size, waiting lists, who holds each budget, the incumbent at each centre and
          the live pipeline. That pass is a starting position, never a conclusion.
        </p>
      )}

      {strategy && (
        <>
          <section className="ar-head card">
            <div className="ar-title">
              <h2>{strategy.title}</h2>
              <div className="ar-tags">
                <span className="pill mute">{strategy.horizon.replace('_', ' ')}</span>
                <span className={`pill ${strategy.status === 'agreed' ? 'ok' : 'warn'}`}>
                  {strategy.status.replace('_', ' ')}
                </span>
                {/* Stated, not implied. A board paper quoting an unreviewed machine draft as
                    company strategy is what this flag exists to prevent. */}
                {strategy.ai_drafted && (
                  <span className="pill risk" title="Generated from the model. Not yet reviewed by a person.">
                    machine-drafted
                  </span>
                )}
              </div>
            </div>
            {strategy.summary && <p className="ar-summary">{strategy.summary}</p>}
            {strategy.draft_basis && (
              <details className="ar-basis">
                <summary>What the draft was reasoning about</summary>
                <p>
                  Without this nobody can tell whether the draft was working from a current
                  model or a stale one.
                </p>
                <dl>
                  {Object.entries(strategy.draft_basis).map(([k, v]) => (
                    <div key={k}>
                      <dt>{k.replace(/_/g, ' ')}</dt>
                      <dd>{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            )}
          </section>

          <ol className="ar-moves">
            {moves.map((m) => (
              <li key={m.id} className="ar-move card">
                <div className="ar-rank">{m.rank}</div>
                <div className="ar-body">
                  <div className="ar-move-head">
                    <h3>{m.title}</h3>
                    <div className="ar-move-tags">
                      {m.ai_drafted && <span className="pill risk">machine-drafted</span>}
                      {m.effort && (
                        <span className={`pill ${EFFORT_TONE[m.effort] ?? 'mute'}`}>{m.effort.replace('_', ' ')} effort</span>
                      )}
                      {m.confidence && <span className="pill mute">{m.confidence} confidence</span>}
                      <span className={`pill ${m.status === 'accepted' || m.status === 'done' ? 'ok' : 'mute'}`}>
                        {m.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  {m.rationale && <p className="ar-rationale">{m.rationale}</p>}
                  <div className="ar-meta">
                    {m.time_horizon && <span>{m.time_horizon}</span>}
                    {m.blocked_by && <span className="ar-blocked">Blocked: {m.blocked_by}</span>}
                  </div>
                  <div className="ar-actions">
                    {canEdit && (
                      <button className="link-btn" onClick={() => setEditing(m)}>Edit</button>
                    )}
                    <button className="link-btn" onClick={() => setSuggesting({ move: m })}>
                      Raise a suggestion
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </>
      )}

      {openSuggestions.length > 0 && (
        <section className="ar-suggestions">
          <h2>Open suggestions <span className="pill accent">{openSuggestions.length}</span></h2>
          <p className="ar-note">
            Raised by anyone. Declining a contribution without a word is how people stop
            contributing, so the database requires a response before one can be closed.
          </p>
          <ul>
            {openSuggestions.map((s) => (
              <li key={s.id}>
                <span className="pill mute">{s.kind.replace('_', ' ')}</span>
                <p>{s.body}</p>
                <span className="ar-when">
                  {new Date(s.raised_at).toLocaleDateString('en-GB',
                    { day: 'numeric', month: 'short' })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {editing && (
        <MoveDialog move={editing} onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }} />
      )}
      {suggesting && (
        <SuggestDialog move={suggesting.move} strategyId={strategy?.id ?? null}
                       onClose={() => setSuggesting(null)}
                       onSaved={() => { setSuggesting(null); reload(); }} />
      )}
    </div>
  );
}

/* The locked door, with both handles on it. */
function NoAccess({ onSuggest, suggesting, onCloseSuggest }: {
  onSuggest: () => void; suggesting: { move: Move | null } | null; onCloseSuggest: () => void;
}) {
  const [asked, setAsked] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="page arena">
      <div className="page-head">
        <h1 className="page-title">Arena</h1>
        <p className="page-sub">The company’s go-to-market strategy.</p>
      </div>
      <section className="ar-locked card">
        <h2>You do not have access to the strategy</h2>
        <p>
          Editing what the company intends to do is deliberately restricted to a small group.
          That is not a judgement about you, and there are two ways through.
        </p>

        <div className="ar-doors">
          <div className="ar-door">
            <h3>Raise a suggestion</h3>
            <p>
              Open to everyone, and the more useful of the two. If you know something the
              strategy should account for, say it here and it goes to the people who own it.
              No access required.
            </p>
            <button className="btn primary" onClick={onSuggest}>Raise a suggestion</button>
          </div>

          <div className="ar-door">
            <h3>Ask for access</h3>
            <p>
              If you should be inside the room rather than passing notes into it, ask. The
              request goes to the owner with whatever reason you give.
            </p>
            {asked ? (
              <p className="ar-asked">Requested. You will hear back from the owner.</p>
            ) : (
              <>
                <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                          placeholder="Why you need it" />
                <button className="btn" disabled={reason.trim().length < 5}
                        onClick={() => requestArenaAccess('read', reason)
                          .then(() => setAsked(true))
                          .catch((e) => setErr(e instanceof Error ? e.message : String(e)))}>
                  Request access
                </button>
              </>
            )}
            {err && <p className="composer-err" role="alert">{err}</p>}
          </div>
        </div>
      </section>

      {suggesting && (
        <SuggestDialog move={null} strategyId={null}
                       onClose={onCloseSuggest} onSaved={onCloseSuggest} />
      )}
    </div>
  );
}

function MoveDialog({ move, onClose, onSaved }: {
  move: Move; onClose: () => void; onSaved: () => void;
}) {
  const ref = useDialog(onClose);
  const [f, setF] = useState<Move>(move);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label="Edit move" ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head"><h2>Edit this move</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="cf-body">
          {move.ai_drafted && (
            <p className="cf-note">
              This move is still machine-drafted. Saving any edit puts your name to it and
              removes that flag.
            </p>
          )}
          <label className="cf-field"><span>Move</span>
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></label>
          <label className="cf-field"><span>Why</span>
            <textarea rows={5} value={f.rationale ?? ''}
                      onChange={(e) => setF({ ...f, rationale: e.target.value })} /></label>
          <div className="cf-row">
            <label className="cf-field"><span>Effort</span>
              <select value={f.effort ?? ''} onChange={(e) => setF({ ...f, effort: e.target.value })}>
                <option value="">Not set</option>
                {['low', 'medium', 'high', 'very_high'].map((x) => <option key={x} value={x}>{x}</option>)}
              </select></label>
            <label className="cf-field"><span>Confidence</span>
              <select value={f.confidence ?? ''} onChange={(e) => setF({ ...f, confidence: e.target.value })}>
                <option value="">Not set</option>
                {['high', 'medium', 'low'].map((x) => <option key={x} value={x}>{x}</option>)}
              </select></label>
            <label className="cf-field"><span>Status</span>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                {['proposed', 'accepted', 'in_flight', 'done', 'rejected', 'parked'].map((x) =>
                  <option key={x} value={x}>{x.replace('_', ' ')}</option>)}
              </select></label>
          </div>
          <label className="cf-field"><span>Blocked by</span>
            <input value={f.blocked_by ?? ''}
                   onChange={(e) => setF({ ...f, blocked_by: e.target.value })} /></label>
          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-save" disabled={busy} onClick={async () => {
              setBusy(true); setErr(null);
              try { await saveMove(f); onSaved(); }
              catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
              finally { setBusy(false); }
            }}>{busy ? 'Saving' : 'Save'}</button>
          </div>
        </div>
      </div>
    </>
  );
}

function SuggestDialog({ move, strategyId, onClose, onSaved }: {
  move: Move | null; strategyId: string | null; onClose: () => void; onSaved: () => void;
}) {
  const ref = useDialog(onClose);
  const [body, setBody] = useState('');
  const [kind, setKind] = useState('comment');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label="Raise a suggestion" ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head"><h2>Raise a suggestion</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="cf-body">
          {move && <p className="cf-note">On: <strong>{move.title}</strong></p>}
          <label className="cf-field"><span>What kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="comment">Comment</option>
              <option value="challenge">Challenge: I think this is wrong</option>
              <option value="new_move">Something missing</option>
              <option value="evidence">Evidence they should see</option>
              <option value="correction">A factual correction</option>
            </select></label>
          <label className="cf-field"><span>Your point</span>
            <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)}
                      placeholder="Nothing here addresses the 340B exposure, and that changes net realisation materially." /></label>
          <p className="cf-note">
            This goes to whoever owns the strategy. They have to respond before they can close
            it: declining a contribution without a word is how people stop contributing.
          </p>
          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-save" disabled={busy || body.trim().length < 10}
                    onClick={async () => {
                      setBusy(true); setErr(null);
                      try {
                        await raiseSuggestion({
                          strategyId, moveId: move?.id ?? null, body, kind,
                        });
                        onSaved();
                      } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
                      finally { setBusy(false); }
                    }}>{busy ? 'Sending' : 'Send'}</button>
          </div>
        </div>
      </div>
    </>
  );
}

export function Arena() {
  return <Boundary what="Arena"><ArenaView /></Boundary>;
}
